import { OrderServiceTalhoesList } from "../../interfaces/Talhao";

export function removerDuplicadosPorOperacao(dados: OrderServiceTalhoesList[]) {
  const map = new Map<string, OrderServiceTalhoesList>();

  dados.forEach(item => {
    const operacoes = item.planoOperacionalOperacaoTalhao
      .map(o => o.operacaoClasse)
      .sort();

    const chave = operacoes.join('|');

    if (!map.has(chave)) {
      map.set(chave, item);
    }
  });

  return Array.from(map.values());
}

export const COLOR_STATUS = {
  planejada: '#2196f3',
  atrasada: '#f44336',
  executadaparcialmente: '#ff9800',
  bloqueada: '#9e9e9e',
  vinculada: '#ff9800',
  executada: '#4caf50',
} as const;

export function separarCamelCase(str: string) {
  if (!str) return '';
  return str.replace(/([a-z])([A-Z])/g, '$1 $2');
}

function isOperacaoSelecionavel(operacaoTalhao: any) {
  return (
    operacaoTalhao &&
    !operacaoTalhao.disabled &&
    operacaoTalhao.planoOperacionalOperacaoTalhaoId &&
    operacaoTalhao.operacaoClasse &&
    operacaoTalhao.operacaoClasse?.toLowerCase() !== 'sem classe' &&
    operacaoTalhao.name !== '-'
  );
}

export function getKeysMarcarTodasDisponiveis(
  orderServiceTalhoes: OrderServiceTalhoesList[] | undefined
): Set<string> {
  const keys = new Set<string>();

  orderServiceTalhoes?.forEach((talhao) => {
    talhao?.planoOperacionalOperacaoTalhao?.forEach((operacaoTalhao: any) => {
      if (!isOperacaoSelecionavel(operacaoTalhao)) return;
      keys.add(operacaoTalhao.planoOperacionalOperacaoTalhaoId);
    });
  });

  return keys;
}

export function todosDisponiveisMarcados(
  orderServiceTalhoes: OrderServiceTalhoesList[] | undefined,
  checkedItems: Set<string>
): boolean {
  const keys = getKeysMarcarTodasDisponiveis(orderServiceTalhoes);
  if (keys.size === 0) return false;
  return Array.from(keys).every(key => checkedItems.has(key));
}

function processarSequencias(lista: any[]) {
  const ultimoPorClasse: any = {};

  const list = lista.map((item) => {
    const classe = item.operacaoClasse;

    const sequenciaAnterior = ultimoPorClasse[classe] ?? 0;

    ultimoPorClasse[classe] = item.sequencia;

    return {
      ...item,
      sequenciaAnterior
    };
  });

  return list;
}

function aplicarRegraDisabled(lista: any[]) {
  const porClasse = new Map<string, any[]>();

  lista.forEach((item) => {
    const classe = item.operacaoClasse ?? '';
    if (!porClasse.has(classe)) {
      porClasse.set(classe, []);
    }
    porClasse.get(classe)!.push(item);
  });

  const disabledPorItem = new Map<any, boolean>();

  porClasse.forEach((itensClasse) => {
    const ordenados = [...itensClasse].sort(
      (a, b) => (a.sequencia ?? 0) - (b.sequencia ?? 0)
    );

    for (let i = 0; i < ordenados.length; i++) {
      const item = ordenados[i];

      if (item.name === '-') {
        disabledPorItem.set(item, true);
        continue;
      }

      const area = item.areaDisponivelPorcentagem ?? 0;

      if (area <= 0) {
        disabledPorItem.set(item, true);
        continue;
      }

      if (area > 0 && area < 100) {
        disabledPorItem.set(item, false);

        let ultimoHabilitado = i;

        for (let j = i + 1; j < ordenados.length; j++) {
          const candidato = ordenados[j];
          if (candidato.name === '-') continue;

          const areaCandidato = candidato.areaDisponivelPorcentagem ?? 0;
          if (areaCandidato <= 0) continue;

          disabledPorItem.set(candidato, false);
          ultimoHabilitado = j;

          if (areaCandidato === 100) break;
        }

        for (let j = ultimoHabilitado + 1; j < ordenados.length; j++) {
          disabledPorItem.set(ordenados[j], true);
        }
        break;
      }

      if (area === 100) {
        disabledPorItem.set(item, false);

        for (let j = i + 1; j < ordenados.length; j++) {
          disabledPorItem.set(ordenados[j], true);
        }
        break;
      }

      disabledPorItem.set(item, true);
    }
  });

  return lista.map((item) => ({
    ...item,
    disabled: disabledPorItem.get(item) ?? true,
    error: false,
  }));
}

export function getDataOrdenacaoMatriz(item: any) {
  const dataRef =
    item?.periodoDataInicio && item?.periodoDataFim
      ? item.periodoDataInicio
      : item?.dataProgramada;

  return dataRef ? new Date(dataRef).getTime() : Infinity;
}

export function organizarOperacoesTalhao(
  lista: any[]
): any[] {
  if (!lista?.length) return [];

  const newList = lista
    ?.sort((a, b) => a.sequencia - b.sequencia)
    ?.sort((a, b) => a.operacaoClasse.localeCompare(b.operacaoClasse));

  const sorted = [...newList].sort(
    (a: any, b: any) => getDataOrdenacaoMatriz(a) - getDataOrdenacaoMatriz(b)
  );

  sorted.forEach((item, index: number) => {
    item.index = index + 1;
  });

  const data = aplicarRegraDisabled(processarSequencias(sorted));

  return data;
}

type Params = {
  operacaoClasse?: string;
};

export function listarOperacoes(
  lista: any[],
  params?: Params
) {
  const { operacaoClasse } = params || {};

  if (operacaoClasse) {
    const newList = lista
      .filter(item => item.operacaoClasse === operacaoClasse)
      .sort((a, b) => a.sequencia - b.sequencia);
    return newList;
  }

  const operacaoBase = lista[0].operacaoClasse;

  const principais = [];
  const secundarios = [];

  for (const item of lista) {
    if (item.operacaoClasse === operacaoBase) {
      principais.push(item);
    } else {
      secundarios.push(item);
    }
  }

  const newList = [
    ...principais,
    ...secundarios,
  ];

  return organizarOperacoesTalhao(newList)
}

function getChaveColuna(plano: any) {
  return plano?.planoOperacionalOperacaoId
    ?? `${plano?.operacaoClasse}-${plano?.operacaoCodigo}-${plano?.sequencia}`;
}

export function addColumnEmpty(list: any[]): OrderServiceTalhoesList[] {
  if (!list?.length) return [];

  // Monta a lista canônica de colunas a partir da união das operações de
  // TODOS os talhões (identificadas por planoOperacionalOperacaoId), ordenada
  // cronologicamente (mesmo critério de data usado em organizarOperacoesTalhao).
  // É normal a coluna alternar de classe e voltar para ela, pois a ordem
  // reflete a data programada/período de cada operação, não o agrupamento
  // por classe. Isso garante que cada coluna represente sempre a MESMA
  // operação em todos os talhões, mesmo quando algum talhão não possui
  // aquela operação específica (em qualquer posição, não só na última).
  const colunasMap = new Map<string, any>();

  list.forEach(item => {
    item?.planoOperacionalOperacaoTalhao?.forEach((plano: any) => {
      const chave = getChaveColuna(plano);
      if (!colunasMap.has(chave)) {
        colunasMap.set(chave, plano);
      }
    });
  });

  const colunas = Array.from(colunasMap.values()).sort(
    (a, b) => getDataOrdenacaoMatriz(a) - getDataOrdenacaoMatriz(b)
  );

  const novo = list.map(item => {
    const planos = item?.planoOperacionalOperacaoTalhao || [];
    const porChave = new Map<string, any>();

    planos.forEach((plano: any) => {
      porChave.set(getChaveColuna(plano), plano);
    });

    const resultadoFinal = colunas.map(coluna => {
      const encontrado = porChave.get(getChaveColuna(coluna));

      if (encontrado) return encontrado;

      return {
        operacaoClasse: coluna.operacaoClasse,
        operacao: coluna.operacao,
        operacaoCodigo: coluna.operacaoCodigo,
        sequencia: coluna.sequencia,
        planoOperacionalOperacaoId: coluna.planoOperacionalOperacaoId,
        dataProgramada: coluna.dataProgramada,
        periodoDataInicio: coluna.periodoDataInicio,
        periodoDataFim: coluna.periodoDataFim,
        name: "-",
        status: "",
        disabled: true
      };
    });

    return {
      ...item,
      planoOperacionalOperacaoTalhao: resultadoFinal
    };
  });

  return novo;
}


export const CHECKED_STATUS = ['Bloqueada', 'Executada', 'Vinculada', ''] as const;


type Talhao = {
  talhaoId?: string;
  planoOperacionalOperacaoTalhao: Operacao[];
}

type Operacao = {
  planoOperacionalOperacaoTalhaoId?: string;
  operacaoClasse?: string;
  disabled?: boolean;
  ordensServico?: number[];
};

type ResultadoValidacao = {
  valido: boolean;
  idsValidos: string[];
  idsInvalidos: string[];
  classesInvalidas: string[];
};

type Operation = {
  isEdit: boolean;
  code: number | null;
  id: string | null;
}

function habilitadaParaEdicao(item: Operacao, operation: Operation) {
  return !!(
    operation.isEdit &&
    operation.code != null &&
    item.ordensServico?.includes(operation.code)
  );
}

function operacoesHabilitadasPorClasse(talhao: Talhao, classe: string, operation: Operation) {
  return (talhao.planoOperacionalOperacaoTalhao || []).filter(
    item =>
      (!item.disabled || habilitadaParaEdicao(item, operation)) &&
      item.operacaoClasse === classe &&
      item.planoOperacionalOperacaoTalhaoId
  );
}

function itemComOrdemServicoAtual(opsClasse: Operacao[], operation: Operation) {
  return opsClasse.find(op => habilitadaParaEdicao(op, operation));
}

function trocaBloqueadaNaEdicao(itemComOS: Operacao | undefined) {
  return !!itemComOS && (itemComOS.ordensServico?.length ?? 0) <= 1;
}

function classeTotalmenteSelecionada(
  lista: Talhao[],
  classe: string,
  idsSelecionados: Set<string>,
  operation: Operation
) {
  let talhoesComClasse = 0;
  let talhoesCompletos = 0;

  for (const talhao of lista) {
    const opsClasse = operacoesHabilitadasPorClasse(talhao, classe, operation);
    if (!opsClasse.length) continue;

    const qtdSelecionada = opsClasse.filter(op =>
      idsSelecionados.has(op.planoOperacionalOperacaoTalhaoId!)
    ).length;

    if (qtdSelecionada > 1) continue;

    talhoesComClasse++;
    if (qtdSelecionada === 1) talhoesCompletos++;
  }

  return { talhoesComClasse, talhoesCompletos };
}

export function validarOperacoes(talhoes: Talhao[], operacoesSelecionadas: Set<string>) {
  if (talhoes.length === 1) return true;

  const selecionadas = talhoes.flatMap(talhao =>
    talhao.planoOperacionalOperacaoTalhao
      .filter(operacao =>
        operacao.planoOperacionalOperacaoTalhaoId &&
        operacoesSelecionadas.has(
          operacao.planoOperacionalOperacaoTalhaoId
        )
      )
      .map(operacao => ({
        ...operacao,
        talhaoId: talhao.talhaoId,
      }))
  );

  for (const selecionada of selecionadas) {
    const mesmaClasseEmOutroTalhao = talhoes.some(talhao => {
      if (talhao.talhaoId === selecionada.talhaoId) {
        return false;
      }

      return talhao.planoOperacionalOperacaoTalhao.some(operacao =>
        operacao.operacaoClasse === selecionada.operacaoClasse &&
        operacao.planoOperacionalOperacaoTalhaoId &&
        operacoesSelecionadas.has(
          operacao.planoOperacionalOperacaoTalhaoId
        )
      );
    });

    if (!mesmaClasseEmOutroTalhao) {
      return false;
    }
  }

  return true;
}

export function todasClassesSelecionadas(
  lista: Talhao[],
  idsSelecionados: Set<string>,
  permitirParcialSemTodosTalhoes: boolean = false,
  operation: Operation = {
    isEdit: false,
    code: null,
    id: '',
  }
): ResultadoValidacao {
  if (idsSelecionados.size === 0) return { valido: true, idsValidos: [], idsInvalidos: [], classesInvalidas: [] };

  const resultado = validarOperacoes(lista, idsSelecionados);
  if (!resultado) {
    return { valido: false, idsValidos: [], idsInvalidos: [], classesInvalidas: [] };
  }

  const mapa = new Map<
    string,
    { habilitados: string[]; selecionados: string[] }
  >();

  for (const talhao of lista) {
    for (const item of talhao.planoOperacionalOperacaoTalhao || []) {
      const habilitado = !item.disabled || habilitadaParaEdicao(item, operation);
      if (habilitado && item.operacaoClasse && item.planoOperacionalOperacaoTalhaoId) {
        if (!mapa.has(item.operacaoClasse)) {
          mapa.set(item.operacaoClasse, {
            habilitados: [],
            selecionados: [],
          });
        }

        const grupo = mapa.get(item.operacaoClasse)!;
        const id = item.planoOperacionalOperacaoTalhaoId;

        grupo.habilitados.push(id);

        if (idsSelecionados.has(id)) {
          grupo.selecionados.push(id);
        }
      }
    }
  }

  const idsValidos: string[] = [];
  const idsInvalidos: string[] = [];
  const classesInvalidas: string[] = [];

  mapa.forEach((grupo, classe) => {
    const selecionados = grupo.selecionados;
    const habilitados = grupo.habilitados;
    if (selecionados.length > 0 && selecionados.length < lista.length) {
      const naoSelecionados = habilitados.filter(h => !selecionados.includes(h));
      idsInvalidos.push(...naoSelecionados);
      classesInvalidas.push(classe);
      return;
    }
  });

  mapa.forEach((grupo, classe) => {
    const selecionados = grupo.selecionados.length;
    if (selecionados === 0) return;

    let classeValida = true;

    const { talhoesComClasse, talhoesCompletos } = classeTotalmenteSelecionada(
      lista,
      classe,
      idsSelecionados,
      operation
    );

    if (permitirParcialSemTodosTalhoes) {
      classeValida = talhoesComClasse > 0;
    } else {
      classeValida = lista.length > 0 && talhoesCompletos === lista.length;
    }

    if (classeValida) {
      idsValidos.push(...grupo.selecionados);
    } else {
      idsInvalidos.push(...grupo.selecionados);
      classesInvalidas.push(classe);
    }
  });

  const idsConflitoMesmaClasse = new Set<string>();
  const classesConflitoMesmaClasse = new Set<string>();

  for (const talhao of lista) {
    const classesDoTalhao = new Set(
      (talhao.planoOperacionalOperacaoTalhao || [])
        .map(item => item.operacaoClasse)
        .filter((classe): classe is string => !!classe)
    );

    classesDoTalhao.forEach((classe) => {
      const opsClasse = operacoesHabilitadasPorClasse(talhao, classe, operation);

      const idsSelecionadosClasse = opsClasse
        .filter(op => idsSelecionados.has(op.planoOperacionalOperacaoTalhaoId!))
        .map(op => op.planoOperacionalOperacaoTalhaoId!);

      if (idsSelecionadosClasse.length === 0) return;

      if (idsSelecionadosClasse.length > 1) {
        idsSelecionadosClasse.forEach((id) => idsConflitoMesmaClasse.add(id));
        classesConflitoMesmaClasse.add(classe);
        return;
      }

      const itemComOS = itemComOrdemServicoAtual(opsClasse, operation);
      const idItemComOS = itemComOS?.planoOperacionalOperacaoTalhaoId;

      if (idItemComOS && !idsSelecionadosClasse.includes(idItemComOS) && trocaBloqueadaNaEdicao(itemComOS)) {
        idsSelecionadosClasse.forEach((id) => idsConflitoMesmaClasse.add(id));
        classesConflitoMesmaClasse.add(classe);
      }
    });
  }

  if (idsConflitoMesmaClasse.size > 0) {
    const idsValidosFinal = idsValidos.filter(id => !idsConflitoMesmaClasse.has(id));
    const idsInvalidosFinal = Array.from(new Set(
      idsInvalidos.concat(Array.from(idsConflitoMesmaClasse))
    ));
    const classesInvalidasFinal = Array.from(new Set(
      classesInvalidas.concat(Array.from(classesConflitoMesmaClasse))
    ));

    return {
      idsValidos: idsValidosFinal,
      idsInvalidos: idsInvalidosFinal,
      classesInvalidas: classesInvalidasFinal,
      valido: idsValidosFinal.length > 0 && idsInvalidosFinal.length === 0 && classesInvalidasFinal.length === 0,
    };
  }

  return {
    idsValidos,
    idsInvalidos,
    classesInvalidas,
    valido: idsValidos.length > 0 && idsInvalidos.length === 0 && classesInvalidas.length === 0,
  };
}
